import { useMemo } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SpeedIcon from '@mui/icons-material/Speed';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getFundingTimingNote } from '../../../utils/loanTimelineFunding';

const UW_DECISION_CHIP = {
  ConditionallyApprove: { label: 'Approved w/ Conditions', color: 'success' },
  ClearToClose: { label: 'Clear to Close', color: 'info' },
  Suspend: { label: 'Suspended', color: 'warning' },
  Deny: { label: 'Denied', color: 'error' },
};

const fmt = (v) => {
  if (v == null || v === '') return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const fmtDay = (v) => {
  if (v == null || v === '') return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** @returns {number | null} Whole days between two ISO-like timestamps */
const wholeDaysBetween = (start, end) => {
  if (!start || !end) return null;
  const a = new Date(start).getTime();
  const b = new Date(end).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
};

const fmtDuration = (start, end) => {
  const d = wholeDaysBetween(start, end);
  if (d == null) return null;
  if (d === 0) return '< 1 day';
  if (d === 1) return '1 day';
  return `${d} days`;
};

const sentAt = (row) => row.sentAt || row.sentTimestamp;

const statusOf = (row) => row.disclosureStatus || row.manualDisclosureStatus || row.status || '—';

/** Earliest valid ISO-like timestamp from a list (for durations & ordering). */
const minTimestamp = (values) => {
  const times = values.filter(Boolean).map((v) => new Date(v).getTime()).filter(Number.isFinite);
  if (!times.length) return null;
  return new Date(Math.min(...times)).toISOString();
};

/** Normalizes .NET-style JSON property names and receipt events onto each row. */
function normalizeDisclosureRow(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const row = {
    ...raw,
    documentType: raw.documentType ?? raw.DocumentType,
    deliveryType: raw.deliveryType ?? raw.DeliveryType,
    status: raw.status ?? raw.Status,
    disclosureStatus: raw.disclosureStatus ?? raw.DisclosureStatus,
    manualDisclosureStatus: raw.manualDisclosureStatus ?? raw.ManualDisclosureStatus,
    createdAt: raw.createdAt ?? raw.CreatedAt,
    sentAt: raw.sentAt ?? raw.SentAt,
    sentTimestamp: raw.sentTimestamp ?? raw.SentTimestamp,
    deliveryId: raw.deliveryId ?? raw.DeliveryId,
    borrowerId: raw.borrowerId ?? raw.BorrowerId,
    borrowerDisplayName: raw.borrowerDisplayName ?? raw.BorrowerDisplayName,
    receivedAt: raw.receivedAt ?? raw.ReceivedAt,
    signedAt: raw.signedAt ?? raw.SignedAt,
    borrowerReceiptEvents: raw.borrowerReceiptEvents ?? raw.BorrowerReceiptEvents,
  };
  const bid = row.borrowerId;
  const events = row.borrowerReceiptEvents;
  if (bid && Array.isArray(events)) {
    const ev = events.find((e) => (e?.borrowerId ?? e?.BorrowerId) === bid) || events[0];
    if (ev) {
      row.receivedAt = row.receivedAt ?? ev.receivedTimestamp ?? ev.ReceivedTimestamp;
      row.signedAt = row.signedAt ?? ev.signedTimestamp ?? ev.SignedTimestamp;
    }
  }
  return row;
}

/** Group rows by deliveryId; newest delivery first (by max createdAt in group). */
function groupRowsByDelivery(rows) {
  if (!rows?.length) return [];
  const map = new Map();
  for (const row of rows) {
    const id = row.deliveryId != null ? String(row.deliveryId) : `row-${map.size}`;
    if (!map.has(id)) map.set(id, []);
    map.get(id).push(row);
  }
  const groups = [...map.values()].map((g) => {
    const created = g.map((r) => new Date(r.createdAt || 0).getTime()).filter(Number.isFinite);
    const newest = created.length ? Math.max(...created) : 0;
    return { rows: g, newest };
  });
  groups.sort((a, b) => b.newest - a.newest);
  return groups.map((g) => g.rows);
}

const DurationChip = ({ start, end, label }) => {
  const text = fmtDuration(start, end);
  if (!text) return null;
  const days = wholeDaysBetween(start, end);
  const isFast = days != null && days <= 3;
  return (
    <Chip
      size="small"
      icon={<ScheduleIcon sx={{ fontSize: '14px !important' }} />}
      label={`${label}: ${text}`}
      color={isFast ? 'success' : 'default'}
      variant={isFast ? 'filled' : 'outlined'}
      sx={{ fontWeight: 600 }}
    />
  );
};

const PhaseCard = ({ title, subtitle, children, durationChip }) => (
  <Card variant="outlined" sx={{ borderLeft: 3, borderLeftColor: 'primary.main', mb: 2 }}>
    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
          {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        </Box>
        {durationChip}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const DisclosureTable = ({ rows, kind }) => {
  if (!rows?.length) {
    return (
      <Typography variant="body2" color="text.secondary">No {kind} delivery data yet.</Typography>
    );
  }
  const deliveryGroups = groupRowsByDelivery(rows);
  return (
    <Stack spacing={2}>
      {deliveryGroups.map((grp, idx) => (
        <Box key={`${kind}-${idx}`}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
            Delivery {deliveryGroups.length > 1 ? `#${deliveryGroups.length - idx}` : ''}{grp[0]?.deliveryId != null ? ` · ID ${grp[0].deliveryId}` : ''}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Borrower</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Received</TableCell>
                <TableCell>Signed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grp.map((row, i) => (
                <TableRow key={`${row.borrowerId || i}-${row.deliveryId}-${i}`}>
                  <TableCell><Typography variant="body2">{row.borrowerDisplayName || row.borrowerId || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{statusOf(row)}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{fmt(row.createdAt)}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{fmt(sentAt(row))}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{fmt(row.receivedAt)}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{fmt(row.signedAt)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}
    </Stack>
  );
};

/**
 * @param {{ loanDetail: object, keyDatesObj?: object }} props
 */
const LoanTimelineTab = ({ loanDetail, keyDatesObj: keyDatesProp }) => {
  const loan = loanDetail?.loan || {};
  const property = loanDetail?.property || {};
  const keyDates = keyDatesProp || {};
  const stateCode = property?.address?.state || loan.propertyState;

  const loanEstimateRows = useMemo(() => {
    const raw = loanDetail?.loanEstimateRows ?? loanDetail?.LoanEstimateRows ?? [];
    return Array.isArray(raw) ? raw.map(normalizeDisclosureRow) : [];
  }, [loanDetail]);

  const closingDisclosureRows = useMemo(() => {
    const raw = loanDetail?.closingDisclosureRows ?? loanDetail?.ClosingDisclosureRows ?? [];
    return Array.isArray(raw) ? raw.map(normalizeDisclosureRow) : [];
  }, [loanDetail]);
  const applicationDate = loan.applicationDate;
  const submittedUff = loan.submittedToUnderwritingDate;
  const uwDecision = loan.underwriterDecision;
  const uwDecisionDate = loan.underwriterDecisionDate;
  const clearedToCloseDate = keyDates.clearedToCloseDate;
  const closingDate = keyDates.closingDate || loan.estimatedCloseDate;
  const fundedDate = keyDates.fundedDate;
  const recordedMortgage = keyDates.recordedMortgageReceivedDate;
  const executedPkg = keyDates.executedClosingPackageReceivedDate;

  const fundingNote = useMemo(() => getFundingTimingNote(stateCode), [stateCode]);

  const firstLeActivity = useMemo(
    () => minTimestamp(loanEstimateRows.flatMap((r) => [r.createdAt, sentAt(r)])),
    [loanEstimateRows],
  );

  const firstCdActivity = useMemo(
    () => minTimestamp(closingDisclosureRows.flatMap((r) => [r.createdAt, sentAt(r)])),
    [closingDisclosureRows],
  );

  return (
    <Box>
      <Alert severity="info" icon={<SpeedIcon />} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700}>How to read this timeline</Typography>
        <Typography variant="body2" color="text.secondary">
          Green duration chips highlight quick handoffs (about three days or less). Longer gaps usually reflect borrower, title, county, or third-party timing—not UFF sitting idle. Disclosure rows come straight from LOS delivery data, as recorded by UFF Ops.
        </Typography>
      </Alert>

      {/* 1 Application / import */}
      <PhaseCard
        title="Loan application / file start"
        subtitle="When the loan record was opened in your pipeline."
        durationChip={submittedUff ? <DurationChip start={applicationDate} end={submittedUff} label="Time to submit" /> : null}
      >
        <Stack spacing={0.5}>
          <Typography variant="body2"><Typography component="span" color="text.secondary">Application date:</Typography> <strong>{fmtDay(applicationDate)}</strong></Typography>
          {loan.lastUpdated && (
            <Typography variant="caption" color="text.secondary">Last updated in portal: {fmt(loan.lastUpdated)}</Typography>
          )}
        </Stack>
      </PhaseCard>

      {/* 2 Submitted to UFF */}
      <PhaseCard
        title="Submitted to UFF"
        subtitle="Loan delivered to UFF for processing and underwriting."
        durationChip={
          submittedUff && loanEstimateRows.length && firstLeActivity
            ? <DurationChip start={submittedUff} end={firstLeActivity} label="Until first LE activity" />
            : submittedUff && clearedToCloseDate
              ? <DurationChip start={submittedUff} end={clearedToCloseDate} label="Until clear to close" />
              : null
        }
      >
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">Submitted to underwriting:</Typography>{' '}
          <strong>{fmtDay(submittedUff)}</strong>
        </Typography>
        {!submittedUff && (
          <Typography variant="caption" color="warning.main">Not yet submitted — loan is still in origination on your side.</Typography>
        )}
      </PhaseCard>

      {/* 3 Loan Estimate */}
      <PhaseCard
        title="Loan Estimate (LE)"
        subtitle="Created, sent, received, and signed — per borrower, per delivery (revised LEs appear as additional deliveries)."
        durationChip={null}
      >
        <DisclosureTable rows={loanEstimateRows} kind="Loan Estimate" />
      </PhaseCard>

      {/* 4 UW */}
      <PhaseCard
        title="Underwriting decision"
        subtitle="Conditional approval and clear to close from UFF underwriting."
        durationChip={
          uwDecisionDate && clearedToCloseDate
            ? <DurationChip start={uwDecisionDate} end={clearedToCloseDate} label="UW approval → CTC" />
            : null
        }
      >
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">UW decision:</Typography>
            {uwDecision ? (
              <Chip
                size="small"
                label={UW_DECISION_CHIP[uwDecision]?.label || uwDecision}
                color={UW_DECISION_CHIP[uwDecision]?.color || 'default'}
                variant="outlined"
              />
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Stack>
          <Typography variant="body2">
            <Typography component="span" color="text.secondary">Decision date:</Typography> <strong>{fmtDay(uwDecisionDate)}</strong>
          </Typography>
          <Typography variant="body2">
            <Typography component="span" color="text.secondary">Clear to close:</Typography> <strong>{fmtDay(clearedToCloseDate)}</strong>
          </Typography>
          {uwDecision === 'ConditionallyApprove' && clearedToCloseDate && (
            <Chip size="small" icon={<CheckCircleOutlineIcon />} label="Reached clear to close after conditional approval" color="success" variant="outlined" />
          )}
        </Stack>
      </PhaseCard>

      {/* 5 Closing Disclosure */}
      <PhaseCard
        title="Closing Disclosure (CD)"
        subtitle="Created, sent, received, and signed — per borrower, per delivery."
        durationChip={
          clearedToCloseDate && closingDisclosureRows.length && firstCdActivity
            ? <DurationChip start={clearedToCloseDate} end={firstCdActivity} label="CTC → first CD activity" />
            : null
        }
      >
        <DisclosureTable rows={closingDisclosureRows} kind="Closing Disclosure" />
      </PhaseCard>

      {/* 6 Docs / recording */}
      <PhaseCard
        title="Documents, recording & funding style"
        subtitle="Executed package, recorded mortgage — timing varies by state (wet vs dry funding)."
        durationChip={
          executedPkg && recordedMortgage
            ? <DurationChip start={executedPkg} end={recordedMortgage} label="Executed → recorded" />
            : closingDate && fundedDate
              ? <DurationChip start={closingDate} end={fundedDate} label="Closing → funded" />
              : null
        }
      >
        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
          {fundingNote}
        </Alert>
        <Stack spacing={0.5}>
          <Typography variant="body2"><Typography component="span" color="text.secondary">Executed closing package received:</Typography> <strong>{fmtDay(executedPkg)}</strong></Typography>
          <Typography variant="body2"><Typography component="span" color="text.secondary">Recorded mortgage received:</Typography> <strong>{fmtDay(recordedMortgage)}</strong></Typography>
          {stateCode && (
            <Typography variant="caption" color="text.secondary">Property state: <strong>{stateCode}</strong> (used for funding context only)</Typography>
          )}
        </Stack>
      </PhaseCard>

      {/* 7 Closing & funded */}
      <PhaseCard
        title="Closing & funded"
        subtitle="Scheduled or actual closing and disbursement."
        durationChip={closingDate && fundedDate ? <DurationChip start={closingDate} end={fundedDate} label="Close → funded" /> : null}
      >
        <Stack spacing={0.5}>
          <Typography variant="body2"><Typography component="span" color="text.secondary">Closing date:</Typography> <strong>{fmtDay(closingDate)}</strong></Typography>
          <Typography variant="body2"><Typography component="span" color="text.secondary">Funded:</Typography> <strong>{fmtDay(fundedDate)}</strong></Typography>
          {keyDates.actualDisbursementDate && (
            <Typography variant="body2"><Typography component="span" color="text.secondary">Actual disbursement:</Typography> <strong>{fmtDay(keyDates.actualDisbursementDate)}</strong></Typography>
          )}
        </Stack>
      </PhaseCard>

    </Box>
  );
};

export default LoanTimelineTab;
